/**
 * useScreenTime
 *
 * Reports the student's current screen-time status so `ScreenTimeGate` can
 * block or warn. It is READ-ONLY.
 *
 * History (2026-09-05): this hook used to measure minutes locally and flush
 * them to `screenTime/{uid}/usage/{date}` via `recordSessionTime`. That made
 * the parental limit self-reported by the party it restricts — a child could
 * report zero minutes and never be blocked. Recorded time is now produced
 * exclusively by the `recordSessionHeartbeat` Cloud Function using the server
 * clock, and `firestore.rules` denies client writes to that document. The
 * local ticker survives only to keep the countdown moving smoothly between
 * server polls; it never persists anything, and `isOverLimit` is always
 * decided by the server-derived numbers.
 *
 * Freshness: the server heartbeat runs every 2 minutes and this hook re-polls
 * every 60 seconds, so a lockout lands within roughly 2 minutes of the limit
 * actually being reached. That is deliberate — the alternative is trusting the
 * client's own clock, which is what made this unenforceable before.
 *
 * Break reminders (2026-09-05): the service used to report a break due only on
 * an exact modulo of recorded minutes, which advance in 2-minute heartbeat
 * steps — so the 15- and 25-minute settings fired at 30 and 50 minutes, and any
 * idle gap skipped a reminder entirely. The service now reports which break
 * INTERVAL the student is in, and this hook raises the reminder when that
 * number goes up. Crossing a boundary can no longer be missed, and a reminder
 * is shown once per interval rather than on every poll inside it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  checkTimeLimit,
  getScreenTimeSettings,
  type ScreenTimeSettings,
  type TimeCheckResult,
} from '../services/screenTimeService';
import { createComponentLogger } from '../utils/logger';

const logger = createComponentLogger('useScreenTime');

export interface ScreenTimeStatus {
  /** Minutes recorded today by the server, plus optimistic local drift. */
  minutesUsed: number;
  /** Minutes left before the daily/weekly limit is hit (never negative). */
  remainingMinutes: number;
  /** True when the student has reached their limit or is outside the schedule. */
  isOverLimit: boolean;
  /** True when a break reminder is due at the configured interval. */
  shouldTakeBreak: boolean;
  /** Reason the session is blocked, when isOverLimit is true. */
  blockReason?: TimeCheckResult['blockReason'];
  /** The loaded settings, or null while loading / when none are configured. */
  settings: ScreenTimeSettings | null;
  /** True until the first status load completes. */
  loading: boolean;
}

/** How often to re-ask the server for authoritative status. */
const POLL_INTERVAL_MS = 60_000;

/** How often the local countdown advances between polls (display only). */
const TICK_INTERVAL_MS = 60_000;

const DEFAULT_STATUS: ScreenTimeStatus = {
  minutesUsed: 0,
  remainingMinutes: 0,
  isOverLimit: false,
  shouldTakeBreak: false,
  blockReason: undefined,
  settings: null,
  loading: true,
};

export function useScreenTime(uid?: string): ScreenTimeStatus {
  const [status, setStatus] = useState<ScreenTimeStatus>(DEFAULT_STATUS);

  // Minutes ticked locally since the last server poll — display smoothing only.
  const localDriftRef = useRef(0);
  // The break interval the student was last shown a reminder for. Starts at -1
  // so a student already past their first interval when the page loads is still
  // reminded once.
  const lastBreakBucketRef = useRef(-1);
  // Guards against state updates after unmount.
  const mountedRef = useRef(true);

  const refreshStatus = useCallback(async () => {
    if (!uid) return;
    try {
      const [check, settings] = await Promise.all([
        checkTimeLimit(uid),
        getScreenTimeSettings(uid),
      ]);
      if (!mountedRef.current) return;

      // The server just told us the truth, so discard accumulated local drift.
      localDriftRef.current = 0;

      // Raise the reminder once per interval crossed, not on every poll while
      // inside one.
      const bucket = check.breakBucket ?? 0;
      const breakDue = check.shouldBreak && bucket > lastBreakBucketRef.current;
      if (breakDue) {
        lastBreakBucketRef.current = bucket;
      }

      const dailyLimit = settings?.dailyLimitMinutes ?? 0;
      const minutesUsed = Math.max(0, dailyLimit - check.remainingMinutes);

      setStatus({
        minutesUsed,
        remainingMinutes: check.remainingMinutes,
        // Never soften the server's verdict. `allowed: false` covers the
        // schedule blocks (outside-days / outside-hours) which are not
        // expressible as a remaining-minutes count.
        isOverLimit: !check.allowed || check.remainingMinutes <= 0,
        shouldTakeBreak: breakDue,
        blockReason: check.blockReason,
        settings: settings ?? null,
        loading: false,
      });
    } catch (error) {
      logger.error('failed to refresh screen-time status', error);
      if (mountedRef.current) {
        // Fail OPEN on a read error. A transient Firestore failure must not
        // lock a child out of their work; the next poll re-establishes truth.
        setStatus((prev) => ({ ...prev, loading: false }));
      }
    }
  }, [uid]);

  // Advance the displayed countdown between polls. Pauses while the tab is
  // hidden, matching the server, which only credits time the tab is visible.
  const tick = useCallback(() => {
    if (typeof document !== 'undefined' && document.hidden) return;

    localDriftRef.current += 1;
    setStatus((prev) => {
      if (!prev.settings) return prev;
      return {
        ...prev,
        minutesUsed: prev.minutesUsed + 1,
        remainingMinutes: Math.max(0, prev.remainingMinutes - 1),
      };
    });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    localDriftRef.current = 0;
    lastBreakBucketRef.current = -1;

    if (!uid) {
      setStatus({ ...DEFAULT_STATUS, loading: false });
      return;
    }

    setStatus((prev) => ({ ...prev, loading: true }));
    void refreshStatus();

    const pollTimer = setInterval(() => void refreshStatus(), POLL_INTERVAL_MS);
    const tickTimer = setInterval(tick, TICK_INTERVAL_MS);

    // Re-sync immediately on return, so time that passed while the tab was
    // backgrounded is reflected without waiting out a poll interval.
    const handleVisibility = () => {
      if (typeof document !== 'undefined' && !document.hidden) {
        void refreshStatus();
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibility);
    }

    return () => {
      mountedRef.current = false;
      clearInterval(pollTimer);
      clearInterval(tickTimer);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibility);
      }
    };
  }, [uid, tick, refreshStatus]);

  return status;
}
